import { useContext, useState, type FC } from 'react';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import { useLocalization } from '@/app/lib/localization/LocalizationProvider.tsx';
import { str } from '@/app/lib/a2ui/props.ts';
import { CalendarDays, Clock3, MapPin } from 'lucide-react';
import { getUpcomingVisitDays, slotInstruction, VISIT_TIME_ZONE, type VisitDay } from '../visit-slots.ts';

export const LakdiVisitPicker: FC<A2uiNodeViewProps> = ({ node }) => {
  const api = useContext(A2uiSurfaceContext);
  const { messageLocale } = useLocalization();
  const visitType = str(node.props.visitType);
  const confirmationNote = str(node.props.confirmationNote);
  const [days] = useState(() => getUpcomingVisitDays());
  const [selectedDay, setSelectedDay] = useState<VisitDay | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const chooseDate = (day: VisitDay) => {
    setSelectedDay(day);
    setSelectedTime(null);
  };

  const chooseTime = (day: VisitDay, time: string) => {
    setSelectedDay(day);
    setSelectedTime(time);
    api?.addToMessage?.({
      id: 'lakdi-preferred-visit-slot',
      label: `Preferred slot: ${day.label} · ${time}`,
      instruction: slotInstruction(messageLocale, day.label, time),
      context: {
        preferredDate: day.isoDate,
        preferredDateLabel: day.label,
        preferredTime: time,
        timeZone: VISIT_TIME_ZONE,
        reservationStatus: 'preference only',
      },
    });
  };

  return (
    <main className="@container/lakdi-visit mx-auto w-full max-w-container-2xl min-w-0">
      <article className="overflow-hidden rounded-3xl border border-border bg-card text-card-foreground shadow-elevated">
        <header className="bg-gradient-to-br from-card via-muted to-primary/10 p-5 @md/lakdi-visit:p-7">
          <div className="flex items-start gap-3">
            <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-card text-primary">
              <CalendarDays className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 space-y-2">
              <h2 className="font-display text-balance text-2xl font-semibold leading-tight tracking-tight text-foreground">
                Choose a preferred visit slot
              </h2>
              {visitType ? (
                <p className="break-words text-base leading-relaxed text-foreground">
                  <span className="font-semibold">Visit type:</span> {visitType}
                </p>
              ) : null}
            </div>
          </div>
          {confirmationNote ? (
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground">{confirmationNote}</p>
          ) : null}

        </header>

        <section className="space-y-5 p-5 @md/lakdi-visit:p-7">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
              <h3 className="text-base font-semibold text-foreground">Choose a date</h3>
            </div>
            <div role="group" aria-label="Preferred date" className="grid grid-cols-2 gap-2 @md/lakdi-visit:grid-cols-3">
              {days.map((day) => {
                const selected = selectedDay?.isoDate === day.isoDate;
                return (
                  <button
                    key={day.isoDate}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => chooseDate(day)}
                    className={`min-h-12 touch-manipulation rounded-xl border px-3 py-2 text-base font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none ${selected ? 'border-primary bg-primary text-primary-foreground shadow-sm' : 'border-border bg-card text-foreground hover:border-primary hover:bg-muted active:bg-accent'}`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedDay ? (
            <div className="space-y-3 border-t border-border pt-5">
              <div className="flex items-center gap-2">
                <Clock3 className="h-5 w-5 text-primary" aria-hidden="true" />
                <h3 className="text-base font-semibold text-foreground">Choose a time</h3>
              </div>
              <div role="group" aria-label="Preferred time" className="grid grid-cols-1 gap-2 @sm/lakdi-visit:grid-cols-3">
                {selectedDay.slots.map((time) => {
                  const selected = selectedTime === time;
                  return (
                    <button
                      key={time}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => chooseTime(selectedDay, time)}
                      className={`min-h-12 touch-manipulation rounded-xl border px-3 py-2 text-base font-medium tabular-nums transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none ${selected ? 'border-primary bg-primary text-primary-foreground shadow-sm' : 'border-border bg-card text-foreground hover:border-primary hover:bg-muted active:bg-accent'}`}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <p aria-live="polite" className="min-h-6 text-base leading-relaxed text-muted-foreground">
            {selectedDay && selectedTime
              ? `Selected preference: ${selectedDay.label} · ${selectedTime}. It is in your message draft; send when ready.`
              : selectedDay
                ? `Selected date: ${selectedDay.label}. Choose a time to add your preference.`
                : 'Choose a date, then choose a time. Your selection is added to the composer, not sent automatically.'}
          </p>
        </section>
      </article>
    </main>
  );
};
